import { config } from "../config";
import { humanRefused, waitForHuman, withHuman } from "../auth/human";
import { keepTrustFrom, withTrustedBrowsers } from "../auth/trustedBrowser";
import { isMobile10 } from "../domain/testMobile";

/**
 * THE SECOND PUBLIC PATH, beside intake.ts, and for the same reason: the
 * caller has no token yet -- they are in the middle of getting one.
 *
 * How the embedded sign-in works, in one paragraph. ZITADEL has no password
 * grant, so the browser cannot trade a password for a token and neither can
 * radaccess. What ZITADEL offers is the contract its own hosted login uses:
 * our app is registered with a loginVersion.loginV2.baseUri of
 * https://ankhos.io/auth, so every authorize request is 302'd to
 * /auth/login?authRequest=V2_… on THIS site; the form posts the credentials to
 * radaccess; radaccess creates a ZITADEL session and finalizes the auth
 * request; ZITADEL answers with our own /callback?code=&state= URL; we
 * navigate there and the ordinary PKCE exchange in auth/Callback.tsx completes.
 * Nothing about tokens, scopes or the backends changes.
 *
 * 🔑 An enrolled authenticator app (TOTP) is asked for ON THIS SITE: after any
 * first factor radaccess may answer 200 {next: "totp", ticket}, and the code
 * goes to verifyTotp on the same ZITADEL session (owner decision 2026-09-15).
 *
 * 🔑 TWO STEPS FOR OPERATOR LOGINS (owner 2026-10-18, radaccess v1.124). The
 * server decides who needs them. A password first may answer
 * {next: "second_factor", ticket, options} -- a code by SMS or email, from
 * the second-step routes below; a code (or a provider) first may answer
 * {next: "password", ticket} -- the password, by finishWithPassword. Both are
 * read in mapSessionAnswer, beside "totp", so every route recognises them.
 *
 * 🚨 The backend refuses to finish a login it cannot finish here (passkey,
 * forced MFA without an authenticator app, password change, non-active user) and
 * answers 409 with the HOSTED login URL for the SAME auth request. The page
 * must send the browser there, not show an error: the hosted UI is the
 * fallback, and it is how those users sign in.
 */

/** Shape of a login-v2 auth request id as measured on the live instance. */
export const AUTH_REQUEST_ID = /^V2_[0-9]{1,32}$/;

export interface AuthRequestInfo {
  authRequestId: string;
  clientId: string;
  loginHint: string;
  promptNone: boolean;
}

/** A verified channel the second step may use. mobile is 10 digits (sms only). */
export interface SecondFactorOption {
  method: "sms" | "email";
  masked: string;
  mobile: string;
}

export type SessionOutcome =
  /** Navigate to callbackUrl; it has been checked to be our own /callback. */
  | { kind: "ok"; callbackUrl: string }
  /** The first factor is good; the authenticator-app code is next (verifyTotp). */
  | { kind: "totp"; ticket: string; expiresIn: number }
  /** The password is good; a code by SMS or email is next. options is never empty. */
  | { kind: "second_factor"; ticket: string; expiresIn: number; options: SecondFactorOption[] }
  /** The code (or provider) is good; the password is next (finishWithPassword). */
  | { kind: "password"; ticket: string; expiresIn: number }
  /** Unknown user or wrong password -- deliberately the same answer. */
  | { kind: "invalid" }
  /** ZITADEL's lockout policy tripped. */
  | { kind: "locked" }
  /** Authenticated, but no grant on the project: not yet approved. */
  | { kind: "forbidden" }
  /** The hosted login must finish this one. hostedLoginUrl has been checked. */
  | { kind: "fallback"; reason: string; hostedLoginUrl: string }
  /** The auth request expired or never existed; start again. */
  | { kind: "expired" }
  /** Rate limited, gateway down, backend not configured, or a refused callback. */
  | { kind: "unavailable"; status: number | null };

/**
 * 🔑 THE CALLBACK IS NOT OPAQUE. radaccess already checks it against the auth
 * request's registered redirect URI and an origin allow-list; this is the
 * browser's own half of the same check. A callback anywhere but our own
 * /callback is refused here even if the backend were wrong.
 */
export function isOwnCallback(url: string, origin: string): boolean {
  try {
    const u = new URL(url, origin);
    return u.origin === origin && u.pathname === "/callback";
  } catch {
    return false;
  }
}

/** The hosted login v2 page for the same auth request, on our authority. */
export function hostedLoginUrl(authority: string, authRequestId: string): string {
  return `${authority.replace(/\/+$/, "")}/ui/v2/login/login?authRequest=${encodeURIComponent(authRequestId)}`;
}

/** A fallback URL must be on the OIDC authority and under its login UI. */
export function isHostedLoginUrl(url: string, authority: string): boolean {
  try {
    const u = new URL(url);
    const a = new URL(authority);
    return u.origin === a.origin && u.pathname.startsWith("/ui/v2/login/");
  } catch {
    return false;
  }
}

/** GET the hint for the form. null when the auth request is gone. */
export async function getAuthRequest(
  authRequestId: string,
  signal?: AbortSignal,
): Promise<AuthRequestInfo | null> {
  if (!AUTH_REQUEST_ID.test(authRequestId)) return null;
  // 🪤 NO `/access` HERE -- gatewayBase already ends in it (see intake.ts).
  const res = await fetch(`${config().gatewayBase}/auth/authrequest/${authRequestId}`, {
    headers: { Accept: "application/json" },
    ...(signal ? { signal } : {}),
  });
  if (res.status === 404 || res.status === 400) return null;
  if (!res.ok) throw new Error(`auth request lookup: HTTP ${res.status}`);
  const body = (await res.json()) as Partial<AuthRequestInfo>;
  return {
    authRequestId,
    clientId: body.clientId ?? "",
    loginHint: body.loginHint ?? "",
    promptNone: body.promptNone === true,
  };
}

export interface SessionInput {
  authRequestId: string;
  loginName: string;
  password: string;
}

/**
 * The E.164 form (+91 and ten digits) of an Indian mobile number typed any of
 * the usual ways, or null.
 *
 * 🔑 THE MOBILE NUMBER IS THE OPERATOR'S IDENTITY (owner decision
 * 2026-09-11). This MUST agree with radaccess's zitadel.NormalizeIndianMobile
 * character for character: the browser uses it for the welcome page's
 * login_hint and to refuse a malformed number before spending a request, the
 * server uses it for the username. login.test.ts carries the same cases as
 * the Go test.
 */
export function normalizeIndianMobile(s: string): string | null {
  const t = s.trim();
  let digits = "";
  for (let i = 0; i < t.length; i++) {
    const c = t.charAt(i);
    if (c >= "0" && c <= "9") digits += c;
    else if (c === "+" && i === 0) continue;
    else if (c === " " || c === "-" || c === "(" || c === ")" || c === ".") continue;
    else return null;
  }
  if (digits.length === 12 && digits.startsWith("91")) digits = digits.slice(2);
  else if (digits.length === 11 && digits.startsWith("0")) digits = digits.slice(1);
  if (!isMobile10(digits)) return null;
  return `+91${digits}`;
}

async function postJSON(path: string, payload: unknown, signal?: AbortSignal) {
  await waitForHuman(path);
  const res = await fetch(`${config().gatewayBase}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    // The human check rides the first step of each way in (auth/human.ts);
    // the trusted-browser tokens ride every sign-in call (auth/trustedBrowser.ts).
    body: JSON.stringify(withHuman(path, withTrustedBrowsers(path, payload))),
    ...(signal ? { signal } : {}),
  });
  const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  humanRefused(path, res.status, body);
  keepTrustFrom(path, body);
  return { status: res.status, body };
}

const retryAfterOf = (body: Record<string, unknown>): number =>
  typeof body.retryAfter === "number" && body.retryAfter > 0 ? body.retryAfter : 60;

export type OtpStartOutcome =
  /** A code was sent -- or, for a number with no account, it looks exactly as if one was. */
  | { kind: "sent"; ticket: string; mobileMasked: string; expiresIn: number; resendAfter: number }
  | { kind: "invalid_mobile" }
  | { kind: "rate_limited"; retryAfter: number }
  /** A non-interactive request was answered with its error callback. */
  | { kind: "ok"; callbackUrl: string }
  | { kind: "expired" }
  | { kind: "unavailable"; status: number | null };

/**
 * Ask for an SMS code. The server answers the same for a number with an
 * account and one without (it will simply never accept a code for the
 * latter), so this cannot tell the page whether the number is registered --
 * by design.
 */
export async function startOtp(
  input: { authRequestId: string; mobile: string },
  signal?: AbortSignal,
): Promise<OtpStartOutcome> {
  if (!AUTH_REQUEST_ID.test(input.authRequestId)) return { kind: "expired" };
  const mobile = normalizeIndianMobile(input.mobile);
  if (mobile === null) return { kind: "invalid_mobile" };
  let r;
  try {
    r = await postJSON("/auth/otp/start", { authRequestId: input.authRequestId, mobile }, signal);
  } catch {
    return { kind: "unavailable", status: null };
  }
  const { status, body } = r;
  if (status === 200 && typeof body.ticket === "string" && body.ticket) {
    return {
      kind: "sent",
      ticket: body.ticket,
      mobileMasked: typeof body.mobileMasked === "string" ? body.mobileMasked : "",
      expiresIn: typeof body.expiresIn === "number" ? body.expiresIn : 300,
      resendAfter: typeof body.resendAfter === "number" ? body.resendAfter : 30,
    };
  }
  if (status === 200 && typeof body.callbackUrl === "string") {
    return isOwnCallback(body.callbackUrl, window.location.origin)
      ? { kind: "ok", callbackUrl: body.callbackUrl }
      : { kind: "unavailable", status: 200 };
  }
  if (status === 400 && body.error === "invalid_mobile") return { kind: "invalid_mobile" };
  if (status === 429) return { kind: "rate_limited", retryAfter: retryAfterOf(body) };
  if (status === 404) return { kind: "expired" };
  return { kind: "unavailable", status };
}

export type CodeVerifyOutcome =
  | SessionOutcome
  | { kind: "ticket_expired" }
  | { kind: "rate_limited"; retryAfter: number };

/** Checks a one-time code at `path`; the SMS and email routes answer alike. */
async function verifyCodeAt(
  path: string,
  input: { authRequestId: string; ticket: string; code: string },
  signal?: AbortSignal,
): Promise<CodeVerifyOutcome> {
  if (!AUTH_REQUEST_ID.test(input.authRequestId)) return { kind: "expired" };
  let r;
  try {
    r = await postJSON(
      path,
      { authRequestId: input.authRequestId, ticket: input.ticket, code: input.code.trim() },
      signal,
    );
  } catch {
    return { kind: "unavailable", status: null };
  }
  if (r.status === 410) return { kind: "ticket_expired" };
  if (r.status === 429) return { kind: "rate_limited", retryAfter: retryAfterOf(r.body) };
  if (r.status === 400 && r.body.error === "invalid_code_format") return { kind: "invalid" };
  return mapSessionAnswer(r.status, r.body);
}

/**
 * Check the SMS code. The answers are those of the password path, plus two
 * that only a code has.
 */
export function verifyOtp(
  input: { authRequestId: string; ticket: string; code: string },
  signal?: AbortSignal,
): Promise<CodeVerifyOutcome> {
  return verifyCodeAt("/auth/otp/verify", input, signal);
}

/** A plain address, as radaccess accepts it (loginemail.go normalizeEmail). */
export function looksLikeEmail(v: string): boolean {
  const s = v.trim();
  return s.length <= 254 && /^[^\s@<>,;]+@[^\s@<>,;]+\.[^\s@<>,;]+$/.test(s);
}

export type EmailStartOutcome =
  /** A code was sent -- or, for an address with no account, it looks exactly as if one was. */
  | { kind: "sent"; ticket: string; emailMasked: string; expiresIn: number; resendAfter: number }
  | { kind: "invalid_email" }
  /** Several accounts hold this verified address: the person must sign in another way (owner 2026-09-19). */
  | { kind: "email_shared" }
  | { kind: "rate_limited"; retryAfter: number }
  | { kind: "ok"; callbackUrl: string }
  | { kind: "expired" }
  | { kind: "unavailable"; status: number | null };

/**
 * Ask for an emailed code (radaccess POST /access/auth/email/start). As for
 * SMS, the answer is the same whether or not the address has an account --
 * except an address SEVERAL accounts hold, which answers 409 email_shared.
 */
export async function startEmail(
  input: { authRequestId: string; email: string },
  signal?: AbortSignal,
): Promise<EmailStartOutcome> {
  if (!AUTH_REQUEST_ID.test(input.authRequestId)) return { kind: "expired" };
  const email = input.email.trim();
  if (!looksLikeEmail(email)) return { kind: "invalid_email" };
  let r;
  try {
    r = await postJSON("/auth/email/start", { authRequestId: input.authRequestId, email }, signal);
  } catch {
    return { kind: "unavailable", status: null };
  }
  const { status, body } = r;
  if (status === 200 && typeof body.ticket === "string" && body.ticket) {
    return {
      kind: "sent",
      ticket: body.ticket,
      emailMasked: typeof body.emailMasked === "string" ? body.emailMasked : "",
      expiresIn: typeof body.expiresIn === "number" ? body.expiresIn : 300,
      resendAfter: typeof body.resendAfter === "number" ? body.resendAfter : 30,
    };
  }
  if (status === 200 && typeof body.callbackUrl === "string") {
    return isOwnCallback(body.callbackUrl, window.location.origin)
      ? { kind: "ok", callbackUrl: body.callbackUrl }
      : { kind: "unavailable", status: 200 };
  }
  if (status === 400 && body.error === "invalid_email") return { kind: "invalid_email" };
  if (status === 409 && body.error === "email_shared") return { kind: "email_shared" };
  if (status === 429) return { kind: "rate_limited", retryAfter: retryAfterOf(body) };
  if (status === 404) return { kind: "expired" };
  return { kind: "unavailable", status };
}

/** Check the emailed code (radaccess POST /access/auth/email/verify). */
export function verifyEmailCode(
  input: { authRequestId: string; ticket: string; code: string },
  signal?: AbortSignal,
): Promise<CodeVerifyOutcome> {
  return verifyCodeAt("/auth/email/verify", input, signal);
}

/**
 * Check the authenticator-app code on the session the first factor proved
 * (radaccess POST /access/auth/totp/verify). The answers are those of
 * verifyOtp: the ticket can expire, attempts are limited, and a good code
 * finishes the login like any other route.
 */
export async function verifyTotp(
  input: { authRequestId: string; ticket: string; code: string },
  signal?: AbortSignal,
): Promise<SessionOutcome | { kind: "ticket_expired" } | { kind: "rate_limited"; retryAfter: number }> {
  if (!AUTH_REQUEST_ID.test(input.authRequestId)) return { kind: "expired" };
  const code = input.code.trim();
  if (!/^[0-9]{6}$/.test(code)) return { kind: "invalid" };
  let r;
  try {
    r = await postJSON("/auth/totp/verify", { authRequestId: input.authRequestId, ticket: input.ticket, code }, signal);
  } catch {
    return { kind: "unavailable", status: null };
  }
  if (r.status === 410) return { kind: "ticket_expired" };
  if (r.status === 429) return { kind: "rate_limited", retryAfter: retryAfterOf(r.body) };
  if (r.status === 400 && r.body.error === "invalid_code_format") return { kind: "invalid" };
  return mapSessionAnswer(r.status, r.body);
}

/** +91 98•••••210 -- enough to recognise one's own number. Mirrors radaccess maskMobile. */
export function maskMobile(e164: string): string {
  if (!/^\+91[0-9]{10}$/.test(e164)) return "";
  return `${e164.slice(0, 3)} ${e164.slice(3, 5)}•••••${e164.slice(10)}`;
}

export type WidgetSignInOutcome =
  | SessionOutcome
  /** MSG91 proved the number, but no active AnkhOS account uses it. */
  | { kind: "no_account" }
  /** The widget token was refused or already used: send a new code. */
  | { kind: "token_rejected" }
  | { kind: "rate_limited"; retryAfter: number }
  /** MSG91 is throttling verification; the same code can be retried shortly. */
  | { kind: "busy"; retryAfter: number };

export type TestCodeSend =
  | { kind: "sent"; requestId: string; resendIn: number }
  | { kind: "refused"; message: string }
  | { kind: "busy"; retryAfter: number }
  | { kind: "unavailable" };

/** A request id from radaccess (a test code or a login code), not from MSG91. */
export const isTestCodeRequest = (id: string) => id.startsWith("test1.") || id.startsWith("code1.");

/** The widget token for a test code: requestId.code. */
export const testCodeAccessToken = (requestId: string, code: string) => `${requestId}.${code.trim()}`;

const TEST_CODE_REFUSALS: Record<string, string> = {
  not_a_test_mobile: "This is not a test number.",
  no_active_account: "No active login uses this test number.",
  not_a_test_operator: "Test numbers get codes only in a test operator.",
};

/**
 * A code for a TEST mobile (radaccess handler/testcode.go, owner 2026-10-24):
 * the sign-in SMS screen and forgot password by SMS. The code is written to
 * the operator's SMS log, never texted, and never comes back here; the page
 * sends requestId.code as the widget access token.
 */
export async function sendTestCode(mobile: string, signal?: AbortSignal): Promise<TestCodeSend> {
  const e164 = normalizeIndianMobile(mobile);
  if (e164 === null) return { kind: "refused", message: TEST_CODE_REFUSALS.not_a_test_mobile! };
  let r;
  try {
    r = await postJSON("/auth/testcode", { mobile: e164 }, signal);
  } catch {
    return { kind: "unavailable" };
  }
  const { status, body } = r;
  if (status === 200 && typeof body.requestId === "string" && body.requestId) {
    return { kind: "sent", requestId: body.requestId, resendIn: typeof body.resendIn === "number" ? body.resendIn : 30 };
  }
  if (status === 429) return { kind: "busy", retryAfter: retryAfterOf(body) };
  const error = typeof body.error === "string" ? body.error : "";
  if (TEST_CODE_REFUSALS[error]) return { kind: "refused", message: TEST_CODE_REFUSALS[error]! };
  if (error.startsWith("captcha_")) return { kind: "refused", message: "Complete the human check, then try again." };
  return { kind: "unavailable" };
}

export type LoginCodeSend =
  | { kind: "sent"; requestId: string; resendIn: number; sms: boolean; email?: string }
  | { kind: "refused"; message: string }
  | { kind: "busy"; retryAfter: number }
  /** The server cannot make codes (503): the page falls back to the SMS widget. */
  | { kind: "unavailable" };

const LOGIN_CODE_REFUSALS: Record<string, string> = {
  no_active_account: "No active login uses this mobile number.",
  no_verified_email: "This login has no verified email address. Choose SMS.",
  not_a_test_operator: "Test numbers get codes only in a test operator.",
  invalid_mobile: "Enter a 10-digit Indian mobile number.",
};

/**
 * A sign-in or reset code by SMS, email or both (radaccess POST /auth/code,
 * owner 2026-10-25). ONE code goes out on each channel chosen; it never comes
 * back here. The page sends requestId.code as the widget access token.
 */
export async function sendLoginCode(
  input: { mobile: string; channels: ("sms" | "email")[]; purpose: "sign_in" | "reset_password" },
  signal?: AbortSignal,
): Promise<LoginCodeSend> {
  const e164 = normalizeIndianMobile(input.mobile);
  if (e164 === null) return { kind: "refused", message: LOGIN_CODE_REFUSALS.invalid_mobile! };
  let r;
  try {
    r = await postJSON("/auth/code", { mobile: e164, channels: input.channels, purpose: input.purpose }, signal);
  } catch {
    return { kind: "refused", message: "The code service could not be reached. Try again." };
  }
  const { status, body } = r;
  if (status === 200 && typeof body.requestId === "string" && body.requestId) {
    const sent = Array.isArray(body.sent) ? body.sent : [];
    return {
      kind: "sent",
      requestId: body.requestId,
      resendIn: typeof body.resendIn === "number" ? body.resendIn : 30,
      sms: sent.includes("sms"),
      ...(typeof body.email === "string" && body.email ? { email: body.email } : {}),
    };
  }
  if (status === 429) return { kind: "busy", retryAfter: retryAfterOf(body) };
  if (status === 503 && body.error === "login_codes_unavailable") return { kind: "unavailable" };
  const error = typeof body.error === "string" ? body.error : "";
  if (LOGIN_CODE_REFUSALS[error]) return { kind: "refused", message: LOGIN_CODE_REFUSALS[error]! };
  if (error.startsWith("captcha_")) return { kind: "refused", message: "Complete the human check, then try again." };
  return { kind: "refused", message: "The code could not be sent. Try again." };
}

/**
 * Finish a sign-in with the access token MSG91's widget returned. radaccess
 * confirms it with MSG91, accepts it once, and completes the login.
 */
export async function signInWithWidget(
  input: { authRequestId: string; mobile: string; accessToken: string },
  signal?: AbortSignal,
): Promise<WidgetSignInOutcome> {
  if (!AUTH_REQUEST_ID.test(input.authRequestId)) return { kind: "expired" };
  const mobile = normalizeIndianMobile(input.mobile);
  if (mobile === null) return { kind: "unavailable", status: null };
  let r;
  try {
    r = await postJSON(
      "/auth/widget",
      { authRequestId: input.authRequestId, mobile, accessToken: input.accessToken },
      signal,
    );
  } catch {
    return { kind: "unavailable", status: null };
  }
  const { status, body } = r;
  if (status === 401) return { kind: "token_rejected" };
  if (status === 404 && body.error === "no_active_account") return { kind: "no_account" };
  if (status === 429 && body.error === "verification_busy") return { kind: "busy", retryAfter: retryAfterOf(body) };
  if (status === 429) return { kind: "rate_limited", retryAfter: retryAfterOf(body) };
  return mapSessionAnswer(status, body);
}

/**
 * POST the credentials. The password goes in the JSON body over TLS to the
 * gateway and nowhere else: not into the URL, not into storage, not into any
 * log line here.
 */
export async function submitPassword(
  input: SessionInput,
  signal?: AbortSignal,
): Promise<SessionOutcome> {
  if (!AUTH_REQUEST_ID.test(input.authRequestId)) return { kind: "expired" };
  let r;
  try {
    r = await postJSON("/auth/session", input, signal);
  } catch {
    return { kind: "unavailable", status: null };
  }
  return mapSessionAnswer(r.status, r.body);
}

/**
 * The channels a second_factor answer offers. An sms entry without a valid
 * mobile cannot run the widget, so it is dropped rather than offered.
 */
function secondFactorOptions(v: unknown): SecondFactorOption[] {
  if (!Array.isArray(v)) return [];
  const out: SecondFactorOption[] = [];
  for (const o of v as Record<string, unknown>[]) {
    if (typeof o !== "object" || o === null) continue;
    const masked = typeof o.masked === "string" ? o.masked : "";
    if (o.method === "email") {
      out.push({ method: "email", masked, mobile: "" });
    } else if (o.method === "sms" && typeof o.mobile === "string") {
      const e164 = normalizeIndianMobile(o.mobile);
      if (e164 !== null) out.push({ method: "sms", masked: masked || maskMobile(e164), mobile: e164.slice(3) });
    }
  }
  return out;
}

/** One mapping for every route that can finish a sign-in. */
function mapSessionAnswer(status: number, body: Record<string, unknown>): SessionOutcome {
  const { oidc } = config();
  switch (status) {
    case 200: {
      if (body.next === "totp" || body.next === "password" || body.next === "second_factor") {
        if (typeof body.ticket !== "string" || !body.ticket) return { kind: "unavailable", status: 200 };
        const expiresIn = typeof body.expiresIn === "number" ? body.expiresIn : 300;
        if (body.next === "second_factor") {
          const options = secondFactorOptions(body.options);
          return options.length > 0
            ? { kind: "second_factor", ticket: body.ticket, expiresIn, options }
            : { kind: "unavailable", status: 200 };
        }
        return { kind: body.next, ticket: body.ticket, expiresIn };
      }
      const cb = typeof body.callbackUrl === "string" ? body.callbackUrl : "";
      if (!isOwnCallback(cb, window.location.origin)) {
        return { kind: "unavailable", status: 200 };
      }
      return { kind: "ok", callbackUrl: cb };
    }
    case 401:
      return { kind: "invalid" };
    case 423:
      return { kind: "locked" };
    case 403:
      return { kind: "forbidden" };
    case 404:
      return { kind: "expired" };
    case 409: {
      const url = typeof body.hostedLoginUrl === "string" ? body.hostedLoginUrl : "";
      // Trust the reason as text; NEVER the URL without the origin check.
      if (!isHostedLoginUrl(url, oidc.authority)) {
        return { kind: "unavailable", status: 409 };
      }
      return {
        kind: "fallback",
        reason: typeof body.reason === "string" ? body.reason : "",
        hostedLoginUrl: url,
      };
    }
    default:
      return { kind: "unavailable", status };
  }
}

/**
 * ------------------------------------------------------------- second step
 *
 * After a first factor answered second_factor or password. Every route takes
 * the sealed ticket that answer carried, and a good second step finishes the
 * login like any first one -- or answers {next: "totp"} for an enrolled
 * authenticator app. 410 means the ticket ran out: the person starts again.
 */

export type SecondEmailStartOutcome =
  /** Use THIS ticket from now on; the one sent is spent. */
  | { kind: "sent"; ticket: string; emailMasked: string; expiresIn: number; resendAfter: number }
  | { kind: "ticket_expired" }
  | { kind: "rate_limited"; retryAfter: number }
  | { kind: "fallback"; reason: string; hostedLoginUrl: string }
  | { kind: "expired" }
  | { kind: "unavailable"; status: number | null };

/** Email the second-step code to the account's verified address. */
export async function startSecondEmail(
  input: { authRequestId: string; ticket: string },
  signal?: AbortSignal,
): Promise<SecondEmailStartOutcome> {
  if (!AUTH_REQUEST_ID.test(input.authRequestId)) return { kind: "expired" };
  let r;
  try {
    r = await postJSON("/auth/second/email/start", { authRequestId: input.authRequestId, ticket: input.ticket }, signal);
  } catch {
    return { kind: "unavailable", status: null };
  }
  const { status, body } = r;
  if (status === 200 && typeof body.ticket === "string" && body.ticket) {
    return {
      kind: "sent",
      ticket: body.ticket,
      emailMasked: typeof body.emailMasked === "string" ? body.emailMasked : "",
      expiresIn: typeof body.expiresIn === "number" ? body.expiresIn : 300,
      resendAfter: typeof body.resendAfter === "number" ? body.resendAfter : 30,
    };
  }
  if (status === 410) return { kind: "ticket_expired" };
  if (status === 429) return { kind: "rate_limited", retryAfter: retryAfterOf(body) };
  if (status === 409 || status === 404) {
    const m = mapSessionAnswer(status, body);
    if (m.kind === "fallback" || m.kind === "expired" || m.kind === "unavailable") return m;
  }
  return { kind: "unavailable", status };
}

/** A second-step email code radaccess sent through radcomm (requestId starts smail1.). */
export const isRadcommEmailRequest = (id: string) => id.startsWith("smail1.");

/** Check a radcomm-emailed second-step code: requestId.code goes as accessToken. */
export async function verifySecondEmailToken(
  input: { authRequestId: string; ticket: string; requestId: string; code: string },
  signal?: AbortSignal,
): Promise<CodeVerifyOutcome> {
  if (!/^[0-9]{6}$/.test(input.code.trim())) return { kind: "invalid" };
  if (!AUTH_REQUEST_ID.test(input.authRequestId)) return { kind: "expired" };
  let r;
  try {
    r = await postJSON(
      "/auth/second/email/verify",
      { authRequestId: input.authRequestId, ticket: input.ticket, accessToken: `${input.requestId}.${input.code.trim()}` },
      signal,
    );
  } catch {
    return { kind: "unavailable", status: null };
  }
  if (r.status === 410) return { kind: "ticket_expired" };
  if (r.status === 429) return { kind: "rate_limited", retryAfter: retryAfterOf(r.body) };
  return mapSessionAnswer(r.status, r.body);
}

/** Check the emailed second-step code. 4–10 digits, or it is refused here. */
export async function verifySecondEmail(
  input: { authRequestId: string; ticket: string; code: string },
  signal?: AbortSignal,
): Promise<CodeVerifyOutcome> {
  if (!/^[0-9]{4,10}$/.test(input.code.trim())) return { kind: "invalid" };
  return verifyCodeAt("/auth/second/email/verify", input, signal);
}

/**
 * The second step's SMS code, made by radaccess and sent through radcomm to
 * the account's own verified phone (POST /auth/second/code, owner
 * 2026-10-25). The ticket stands in for the human check. The page sends
 * requestId.code to secondWithWidget; "unavailable" falls back to the widget.
 */
export async function sendSecondCode(
  input: { authRequestId: string; ticket: string; channel?: "sms" | "email" | "both" },
  signal?: AbortSignal,
): Promise<LoginCodeSend> {
  let r;
  try {
    r = await postJSON("/auth/second/code", input, signal);
  } catch {
    return { kind: "refused", message: "The code service could not be reached. Try again." };
  }
  const { status, body } = r;
  if (status === 200 && typeof body.requestId === "string" && body.requestId) {
    return {
      kind: "sent",
      requestId: body.requestId,
      resendIn: typeof body.resendIn === "number" ? body.resendIn : 30,
      sms: input.channel !== "email",
      ...(typeof body.email === "string" && body.email ? { email: body.email } : {}),
    };
  }
  if (status === 429) return { kind: "busy", retryAfter: retryAfterOf(body) };
  if (status === 503) return { kind: "unavailable" };
  if (status === 410) return { kind: "refused", message: "This sign-in took too long. Start again." };
  return { kind: "refused", message: "The code could not be sent. Try again." };
}

/**
 * Finish the second step with the token MSG91's widget returned for the
 * mobile the second_factor answer offered. radaccess checks it is THAT mobile.
 */
export async function secondWithWidget(
  input: { authRequestId: string; ticket: string; accessToken: string },
  signal?: AbortSignal,
): Promise<WidgetSignInOutcome | { kind: "ticket_expired" }> {
  if (!AUTH_REQUEST_ID.test(input.authRequestId)) return { kind: "expired" };
  let r;
  try {
    r = await postJSON("/auth/second/widget", input, signal);
  } catch {
    return { kind: "unavailable", status: null };
  }
  const { status, body } = r;
  // widget_token_rejected and widget_token_used: either way, a new code.
  if (status === 401) return { kind: "token_rejected" };
  if (status === 410) return { kind: "ticket_expired" };
  if (status === 429 && body.error === "verification_busy") return { kind: "busy", retryAfter: retryAfterOf(body) };
  if (status === 429) return { kind: "rate_limited", retryAfter: retryAfterOf(body) };
  return mapSessionAnswer(status, body);
}

/** The password, after a code (or a provider) proved the first step. */
export async function finishWithPassword(
  input: { authRequestId: string; ticket: string; password: string },
  signal?: AbortSignal,
): Promise<CodeVerifyOutcome> {
  if (!AUTH_REQUEST_ID.test(input.authRequestId)) return { kind: "expired" };
  let r;
  try {
    r = await postJSON("/auth/second/password", input, signal);
  } catch {
    return { kind: "unavailable", status: null };
  }
  if (r.status === 410) return { kind: "ticket_expired" };
  if (r.status === 429) return { kind: "rate_limited", retryAfter: retryAfterOf(r.body) };
  return mapSessionAnswer(r.status, r.body);
}

/**
 * ---------------------------------------------------------------- providers
 *
 * Signing in with Google (or any identity provider active on the login org)
 * and LINKING it to an existing AnkhOS account -- step 4 of the sign-in design
 * (radaccess handler/loginidp.go; owner decisions 2026-09-18 and 2026-09-19).
 *
 * A provider NEVER creates an account. When the external account is linked to
 * nobody, radaccess answers `next: "link"` with a link ticket, and the person
 * must prove the AnkhOS account by a code to its own mobile or email before
 * anything is written. Every refusal of a proof reads alike: one message, and
 * nothing linked.
 */

export interface IdentityProvider {
  id: string;
  name: string;
  type: string;
}

export type IdpListOutcome =
  | { kind: "ok"; providers: IdentityProvider[] }
  | { kind: "expired" }
  | { kind: "unavailable"; status: number | null };

/** The providers the sign-in page may offer for this auth request. */
export async function listIdps(
  input: { authRequestId: string },
  signal?: AbortSignal,
): Promise<IdpListOutcome> {
  if (!AUTH_REQUEST_ID.test(input.authRequestId)) return { kind: "expired" };
  let r;
  try {
    r = await postJSON("/auth/idp/list", { authRequestId: input.authRequestId }, signal);
  } catch {
    return { kind: "unavailable", status: null };
  }
  if (r.status === 404) return { kind: "expired" };
  if (r.status !== 200 || !Array.isArray(r.body.providers)) {
    return { kind: "unavailable", status: r.status };
  }
  const providers = (r.body.providers as Record<string, unknown>[])
    .filter((p) => typeof p.id === "string" && p.id)
    .map((p) => ({
      id: p.id as string,
      name: typeof p.name === "string" && p.name ? (p.name as string) : "Sign-in provider",
      type: typeof p.type === "string" ? (p.type as string) : "",
    }));
  return { kind: "ok", providers };
}

export type IdpStartOutcome =
  | { kind: "redirect"; authUrl: string; ticket: string }
  | { kind: "expired" }
  | { kind: "unavailable"; status: number | null };

/**
 * Start an external sign-in. The ticket must survive the round trip to the
 * provider, so the caller keeps it (sessionStorage) until /signin/idp.
 */
export async function startIdp(
  input: { authRequestId: string; idpId: string },
  signal?: AbortSignal,
): Promise<IdpStartOutcome> {
  if (!AUTH_REQUEST_ID.test(input.authRequestId)) return { kind: "expired" };
  let r;
  try {
    r = await postJSON("/auth/idp/start", input, signal);
  } catch {
    return { kind: "unavailable", status: null };
  }
  if (r.status === 404) return { kind: "expired" };
  const url = typeof r.body.authUrl === "string" ? r.body.authUrl : "";
  const ticket = typeof r.body.ticket === "string" ? r.body.ticket : "";
  // 🔑 The provider URL is NOT our own origin, so isOwnCallback cannot apply.
  // It must still be an absolute https URL: anything else (javascript:, a
  // relative path) is refused rather than handed to the browser.
  if (r.status !== 200 || !ticket || !/^https:\/\/[^\s]+$/i.test(url)) {
    return { kind: "unavailable", status: r.status };
  }
  return { kind: "redirect", authUrl: url, ticket };
}

export type IdpFinishOutcome =
  | SessionOutcome
  /** Linked to nobody: prove the account, then it is linked. */
  | { kind: "link"; linkTicket: string; provider: string; expiresIn: number }
  /** The provider did not say the email is verified: nothing can be linked. */
  | { kind: "provider_email_unverified" }
  /** The external sign-in failed or was refused. */
  | { kind: "idp_failed" }
  | { kind: "ticket_expired" };

/** Finish an external sign-in with what the provider returned. */
export async function finishIdp(
  input: { authRequestId: string; ticket: string; intentId: string; intentToken: string },
  signal?: AbortSignal,
): Promise<IdpFinishOutcome> {
  if (!AUTH_REQUEST_ID.test(input.authRequestId)) return { kind: "expired" };
  let r;
  try {
    r = await postJSON("/auth/idp/finish", input, signal);
  } catch {
    return { kind: "unavailable", status: null };
  }
  const { status, body } = r;
  if (status === 200 && body.next === "link") {
    return typeof body.linkTicket === "string" && body.linkTicket
      ? {
          kind: "link",
          linkTicket: body.linkTicket,
          provider: typeof body.provider === "string" && body.provider ? body.provider : "this sign-in",
          expiresIn: typeof body.expiresIn === "number" ? body.expiresIn : 600,
        }
      : { kind: "unavailable", status: 200 };
  }
  if (status === 410) return { kind: "ticket_expired" };
  if (status === 403 && body.error === "provider_email_unverified") {
    return { kind: "provider_email_unverified" };
  }
  if (status === 401) return { kind: "idp_failed" };
  if (status === 503) return { kind: "unavailable", status };
  return mapSessionAnswer(status, body);
}

/** Ask for an emailed code that proves the account this link will be made on. */
export async function startLinkEmail(
  input: { authRequestId: string; linkTicket: string; email: string },
  signal?: AbortSignal,
): Promise<EmailStartOutcome> {
  if (!AUTH_REQUEST_ID.test(input.authRequestId)) return { kind: "expired" };
  const email = input.email.trim();
  if (!looksLikeEmail(email)) return { kind: "invalid_email" };
  let r;
  try {
    r = await postJSON(
      "/auth/idp/link/email/start",
      { authRequestId: input.authRequestId, linkTicket: input.linkTicket, email },
      signal,
    );
  } catch {
    return { kind: "unavailable", status: null };
  }
  const { status, body } = r;
  if (status === 200 && typeof body.ticket === "string" && body.ticket) {
    return {
      kind: "sent",
      ticket: body.ticket,
      emailMasked: typeof body.emailMasked === "string" ? body.emailMasked : "",
      expiresIn: typeof body.expiresIn === "number" ? body.expiresIn : 300,
      resendAfter: typeof body.resendAfter === "number" ? body.resendAfter : 30,
    };
  }
  if (status === 400 && body.error === "invalid_email") return { kind: "invalid_email" };
  if (status === 409 && body.error === "email_shared") return { kind: "email_shared" };
  if (status === 410) return { kind: "expired" };
  if (status === 429) return { kind: "rate_limited", retryAfter: retryAfterOf(body) };
  if (status === 404) return { kind: "expired" };
  return { kind: "unavailable", status };
}

export type LinkOutcome =
  | CodeVerifyOutcome
  /** The account has no verified email, so the required notice cannot reach it. */
  | { kind: "account_email_unverified" }
  /** The notice could not be sent, or the external account is already linked. */
  | { kind: "link_refused" };

function mapLinkAnswer(status: number, body: Record<string, unknown>): LinkOutcome {
  if (status === 410) return { kind: "ticket_expired" };
  if (status === 429) return { kind: "rate_limited", retryAfter: retryAfterOf(body) };
  if (status === 403 && body.error === "account_email_unverified") {
    return { kind: "account_email_unverified" };
  }
  // 409 link_refused, 503 notice_unavailable, 403 link_refused: one answer to
  // the person, because the difference is not theirs to act on.
  if (status === 409 || status === 503 || (status === 403 && body.error === "link_refused")) {
    return { kind: "link_refused" };
  }
  return mapSessionAnswer(status, body);
}

/** Prove the account by the emailed code; on success the provider is linked. */
export async function linkWithEmailCode(
  input: { authRequestId: string; linkTicket: string; ticket: string; code: string },
  signal?: AbortSignal,
): Promise<LinkOutcome> {
  if (!AUTH_REQUEST_ID.test(input.authRequestId)) return { kind: "expired" };
  let r;
  try {
    r = await postJSON(
      "/auth/idp/link/email/verify",
      { ...input, code: input.code.trim() },
      signal,
    );
  } catch {
    return { kind: "unavailable", status: null };
  }
  if (r.status === 400 && r.body.error === "invalid_code_format") return { kind: "invalid" };
  return mapLinkAnswer(r.status, r.body);
}

/** Prove the account by the mobile the MSG91 widget just verified. */
export async function linkWithWidget(
  input: { authRequestId: string; linkTicket: string; mobile: string; accessToken: string },
  signal?: AbortSignal,
): Promise<LinkOutcome> {
  if (!AUTH_REQUEST_ID.test(input.authRequestId)) return { kind: "expired" };
  const mobile = normalizeIndianMobile(input.mobile);
  if (mobile === null) return { kind: "unavailable", status: null };
  let r;
  try {
    r = await postJSON(
      "/auth/idp/link/widget",
      { authRequestId: input.authRequestId, linkTicket: input.linkTicket, mobile, accessToken: input.accessToken },
      signal,
    );
  } catch {
    return { kind: "unavailable", status: null };
  }
  if (r.status === 401) return { kind: "invalid" };
  if (r.status === 404 && r.body.error === "no_active_account") return { kind: "link_refused" };
  return mapLinkAnswer(r.status, r.body);
}
